import { getHeadersWithToken, occupationTypesList, segmentTypesList } from "./api";
import { pushEvent } from "./analytics";
import { showToast } from "./toast";
import { FlyoutManager } from "./flyoutManager";

export function initRegister() {
    const flyout = document.querySelector('[data-flyout="registerForm"]');
    const passwordInput = flyout?.querySelector('#register-password');
    const confirmPasswordInput = flyout?.querySelector('#confirmPassword');
    const eyeIcons = flyout?.querySelectorAll(".register-flyout__eye-icon") || [];
    const submitButton = flyout?.querySelector('button[type="submit"]');
    const emailInput = flyout.querySelector("#email");
    const zipInput = flyout.querySelector("#zip");
    const emailError = flyout.querySelector('[data-error-for="email"]');
    const generalErrorDiv = flyout.querySelector('.alert.alert-error');
    
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    eyeIcons.forEach(icon => {
        const input = icon.previousElementSibling;
        if (!input) return;

        icon.addEventListener("click", () => {
            const isPassword = input.type === "password";
            input.type = isPassword ? "text" : "password";
            icon.alt = isPassword ? "Hide password" : "Show password";
            icon.src = isPassword ? "/images/eye-icon-hide.svg" : "/images/eye-icon.svg";
        });
    });

    const requiredFields = [
        "#fname", "#lname", "#email", "#zip", "#register-password",
        "#confirmPassword", "#occupation"
    ].map(selector => flyout?.querySelector(selector)).filter(Boolean);
    
    const segmentsMultipleField = flyout?.querySelector(".segment-select-multiple");

    async function populateDropdowns() {
        try {
            const [occupations, segments] = await Promise.all([
                occupationTypesList(),
                segmentTypesList()
            ]);

            const occupationSelect = flyout.querySelector("#occupation");
            if (occupationSelect) {
                occupations.forEach(item => {
                    const option = document.createElement('option');
                    option.text = item.name;
                    option.value = item.name;
                    occupationSelect.appendChild(option);
                });
            }

            const segmentSelect = flyout.querySelector(".segment-select");
            const segmentsMultiple = flyout.querySelector(".segment-select-multiple");
            if (segmentSelect && segmentsMultiple) {
                segments.forEach(item => {
                    // Populate visible dropdown
                    const visibleOption = document.createElement('option');
                    visibleOption.text = item.name;
                    visibleOption.value = item.id;
                    segmentSelect.appendChild(visibleOption);
                    
                    // Populate hidden multi-select
                    const hiddenOption = document.createElement('option');
                    hiddenOption.text = item.name;
                    hiddenOption.value = item.id;
                    segmentsMultiple.appendChild(hiddenOption);
                });
            }
        } catch (err) {
            console.error("Failed to load registration form data:", err);
        }
    }

    populateDropdowns().catch(err => console.error("Failed to load registration form data:", err));

    function showError(field, message) {
        field.classList.add("error");
        const errorEl = flyout.querySelector(`.error-message[data-error-for="${field.id}"]`);
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.classList.add("visible");
        }
    }

    function clearError(field) {
        field.classList.remove("error");
        const errorEl = flyout.querySelector(`.error-message[data-error-for="${field.id}"]`);
        if (errorEl) {
            errorEl.textContent = "";
            errorEl.classList.remove("visible");
        }
    }

    const toggleSubmitState = () => {
        const allFilled = requiredFields.every(field => {
            if (field.tagName === "SELECT") {
                return field.value && field.selectedIndex !== 0;
            }
            return field.value.trim().length > 0;
        });
        
        // Also check if at least one segment is selected
        const hasSegment = segmentsMultipleField && segmentsMultipleField.selectedOptions.length > 0;
        
        // Validate email format
        const emailValid = emailInput && emailPattern.test(emailInput.value.trim());
        
        submitButton.disabled = !(allFilled && hasSegment && emailValid);
    };

    requiredFields.forEach(field => {
        const eventType = field.tagName === "SELECT" ? "change" : "input";
        field.addEventListener(eventType, () => {
            clearError(field);
            toggleSubmitState();
        });
    });
    
    // Add blur validation for email field
    if (emailInput) {
        emailInput.addEventListener("blur", () => {
            const email = emailInput.value.trim();
            if (email && !emailPattern.test(email)) {
                showError(emailInput, "Please enter a valid email address.");
            } else if (email) {
                clearError(emailInput);
            }
            toggleSubmitState();
        });
    }
    
    // Listen to changes on the segment multi-select
    if (segmentsMultipleField) {
        segmentsMultipleField.addEventListener("change", () => {
            toggleSubmitState();
        });
    }

    flyout?.querySelector("form")?.addEventListener("submit", async (e) => {
        e.preventDefault();

        const firstName = flyout.querySelector("#fname")?.value.trim();
        const lastName = flyout.querySelector("#lname")?.value.trim();
        const email = emailInput?.value.trim();
        const password = passwordInput?.value.trim();
        const confirmPassword = confirmPasswordInput?.value.trim();
        const zip = zipInput?.value.trim();
        const occupationSelect = flyout.querySelector("#occupation");
        const segmentsMultiple = flyout.querySelector(".segment-select-multiple");
        const allowMarketing = flyout.querySelector("#optin")?.checked ?? false;

        const occupation = occupationSelect && occupationSelect.selectedIndex !== 0 ? occupationSelect.value : null;
        const segments = segmentsMultiple && segmentsMultiple.selectedOptions.length > 0
            ? Array.from(segmentsMultiple.selectedOptions).map(opt => opt.value) 
            : [];

        let valid = true;

        requiredFields.forEach(field => {
            clearError(field);
        });

        requiredFields.forEach(field => {
            const value = field.value.trim();
            if (!value) {
                showError(field, "This field is required.");
                valid = false;
            }
        });

        if (!emailPattern.test(email)) {
            showError(emailInput, "Please enter a valid email address.");
            valid = false;
        }

        if (password !== confirmPassword) {
            showError(confirmPasswordInput, "Passwords do not match.");
            valid = false;
        }

        const zipPattern = /^((\d{5}(-\d{4})?)|[a-z0-9]{3}\s[a-z0-9]{3})$/i;
        if (!zipPattern.test(zip)) {
            showError(zipInput, "Please enter a valid ZIP code.");
            valid = false;
        }

        if (!valid) return;

        submitButton.disabled = true;
        submitButton.textContent = "Creating Account...";

        try {
            const payload = {
                firstName,
                lastName,
                email,
                password,
                confirmPassword,
                occupation,
                segments,
                allowMarketing,
                zip
            };

            const response = await fetch("/api/Accounts/Register", {
                method: "POST",
                credentials: "include",
                headers: await getHeadersWithToken({
                    "Content-Type": "application/json",
                }),
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                // GTM: Track successful account creation
                pushEvent({
                    'event': 'formSubmission',
                    'formID': 'createAccount'
                });

                showToast("Account created, check your email for a verification link.", "success");
                FlyoutManager.close("registerForm");
            } else {
                let errorMessage = "Registration failed.";

                try {
                    const result = await response.json();

                    if (result?.message) {
                        errorMessage = result.message;
                    }

                    if (result?.errors) {
                        requiredFields.forEach(field => clearError(field));
                        if (generalErrorDiv) generalErrorDiv.textContent = "";

                        for (const [key, messages] of Object.entries(result.errors)) {
                            if (key === "*") {
                                // General errors - show in alert div
                                if (generalErrorDiv) {
                                    generalErrorDiv.textContent = messages.join(" ");
                                }
                            } else if (key.toLowerCase() === "password") {
                                showError(passwordInput, messages.join(" "));
                            } else if (key.toLowerCase() === "email") {
                                showError(emailInput, messages.join(" "));
                            } else if (key.toLowerCase() === "zip") {
                                showError(zipInput, messages.join(" "));
                            } else {
                                const field = flyout.querySelector(`#${key.toLowerCase()}`);
                                if (field) {
                                    showError(field, messages.join(" "));
                                } else {
                                    console.warn(`Unhandled validation error for "${key}":`, messages);
                                }
                            }
                        }

                        return;
                    }
                } catch (jsonErr) {
                    const text = await response.text();
                    console.warn("Unexpected error response (non-JSON):", text);
                }

                showToast(errorMessage, "error");
            }
        } catch (err) {
            console.error("Registration error:", err);
            showToast("An unexpected error occurred. Please try again.", "error");
        } finally {
            submitButton.disabled = false;
            submitButton.textContent = "Create Account";
        }
    });
}
